// CohortsPage — native Nimbus. Presentation-only migration from shadcn/ui.
// Data/state/service logic is byte-identical to the original; only the UI layer is Nimbus.
import { useState, useEffect } from 'react'
import {
  Button, Tag, Card, Input, Label, Select, Modal, Icon, Title, Text, Tabs, Checkbox, Alert,
} from '@nimbus-ds/components'
import {
  PlusIcon, TrashIcon, UserGroupIcon, StatsIcon, ArrowupIcon, ArrowDownIcon, ArrowRightIcon,
  ExclamationTriangleIcon, CheckCircleIcon, ArchiveIcon,
} from '@nimbus-ds/icons'
import { useToast } from '@/hooks/use-toast'
import {
  cohortService,
  type CohortDefinition,
  type CohortCondition,
  type CohortComparisonResult,
} from '@/api/cohorts'
import { funnelService } from '@/api/funnel'
import { experimentService } from '@/api/experiments'
import { listCustomEvents, type CustomEventDefinition } from '@/api/customEvents'
import { authService } from '@/api/auth'
import type { FunnelStep, Experiment } from '@/types'

function getInsignificanceReason(
  pw: { sessions: number; conversions: number; p_value: number; lift_percentage: number },
  baselineSessions: number,
  baselineConversions: number
): string {
  const totalSessions = pw.sessions + baselineSessions
  const totalConversions = pw.conversions + baselineConversions
  const minCohortSessions = Math.min(pw.sessions, baselineSessions)

  if (totalConversions < 20) {
    return `Solo hay ${totalConversions} conversiones entre ambas cohortes. Se necesitan al menos ~30-50 conversiones para detectar diferencias reales.`
  }
  if (minCohortSessions < 100) {
    return `La cohorte más chica tiene solo ${minCohortSessions} sesiones. Se necesitan más datos en ambas cohortes para que la comparación sea confiable.`
  }
  if (Math.abs(pw.lift_percentage) < 10) {
    return `El lift de ${pw.lift_percentage > 0 ? '+' : ''}${pw.lift_percentage.toFixed(1)}% es pequeño. Para detectar diferencias chicas se necesitan muchas más sesiones.`
  }
  return `Con ${totalSessions} sesiones totales y p = ${pw.p_value.toFixed(2)}, aún no hay suficientes datos para confirmar que la diferencia no es azar.`
}

const CONDITION_TYPE_LABELS: Record<string, string> = {
  funnel_step: 'Paso del funnel',
  not_funnel_step: 'No alcanzó paso',
  experiment_cohort: 'Cohorte de experimento',
  converted: 'Convirtió',
  not_converted: 'No convirtió',
  device: 'Dispositivo',
  entry_source: 'Fuente de entrada',
  entry_step: 'Paso de entrada',
  custom_event: 'Evento personalizado',
  not_custom_event: 'Sin evento personalizado',
  pageview_pattern: 'Patrón de URL',
}

const DEVICE_OPTIONS = ['mobile', 'desktop', 'tablet']
const ENTRY_SOURCE_OPTIONS = [
  'direct', 'google', 'facebook', 'instagram', 'meta_ads',
  'other_search', 'other_external', 'category', 'search',
  'sale_page', 'product_browsing', 'homepage', 'other_internal',
]

function conditionSummary(
  conditions: CohortCondition[],
  funnelSteps?: FunnelStep[],
  experiments?: Experiment[],
  customEvents?: CustomEventDefinition[],
): string {
  return conditions.map(c => {
    const label = CONDITION_TYPE_LABELS[c.type] || c.type
    if (c.step_id) {
      const step = funnelSteps?.find(s => String(s.id) === c.step_id)
      return `${label}: ${step?.name || c.step_id}`
    }
    if (c.event_name) {
      const evt = customEvents?.find(e => e.event_name === c.event_name)
      return `${label}: ${evt?.display_name || c.event_name}`
    }
    if (c.value) return `${label}: ${c.value}`
    if (c.pattern) return `${label}: ${c.pattern}`
    if (c.experiment_id) {
      const exp = experiments?.find(e => e.id === c.experiment_id)
      return `${label}: ${exp?.name || c.experiment_id} (${c.variant})`
    }
    return label
  }).join(' + ')
}

function formatRate(rate: number): string {
  return `${(rate * 100).toFixed(2)}%`
}

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS', maximumFractionDigits: 0 }).format(amount)
}

// --- Condition Row Builder ---
function ConditionRow({
  condition,
  index,
  onChange,
  onRemove,
  funnelSteps,
  experiments,
  customEvents,
}: {
  condition: CohortCondition
  index: number
  onChange: (index: number, cond: CohortCondition) => void
  onRemove: (index: number) => void
  funnelSteps: FunnelStep[]
  experiments: Experiment[]
  customEvents: CustomEventDefinition[]
}) {
  const needsStepId = ['funnel_step', 'not_funnel_step', 'entry_step'].includes(condition.type)
  const needsEventName = ['custom_event', 'not_custom_event'].includes(condition.type)
  const needsPattern = condition.type === 'pageview_pattern'
  const needsDevice = condition.type === 'device'
  const needsEntrySource = condition.type === 'entry_source'
  const needsExperiment = condition.type === 'experiment_cohort'
  const noValueNeeded = ['converted', 'not_converted'].includes(condition.type)

  // Get variant options for selected experiment
  const selectedExperiment = experiments.find(e => e.id === condition.experiment_id)
  const variantOptions = selectedExperiment
    ? Object.keys(selectedExperiment.traffic_split)
    : []

  return (
    <div className="flex items-center gap-2">
      {index > 0 && (
        <div className="shrink-0 whitespace-nowrap"><Tag appearance="neutral">Y</Tag></div>
      )}

      <div className="w-[200px]">
        <Select
          id={`cond-${index}-type`}
          name={`cond-${index}-type`}
          value={condition.type}
          onChange={(e) => onChange(index, { type: e.target.value })}
        >
          {Object.entries(CONDITION_TYPE_LABELS).map(([value, label]) => (
            <Select.Option key={value} value={value} label={label} />
          ))}
        </Select>
      </div>

      {needsStepId && (
        <div className="flex-1">
          <Select
            id={`cond-${index}-step`}
            name={`cond-${index}-step`}
            value={condition.step_id || ''}
            onChange={(e) => onChange(index, { ...condition, step_id: e.target.value })}
          >
            <Select.Option value="" label="Seleccionar paso" />
            {funnelSteps.map(step => (
              <Select.Option key={step.id} value={String(step.id)} label={step.name} />
            ))}
          </Select>
        </div>
      )}

      {needsEventName && (
        <div className="flex-1">
          <Select
            id={`cond-${index}-event`}
            name={`cond-${index}-event`}
            value={condition.event_name || ''}
            onChange={(e) => onChange(index, { ...condition, event_name: e.target.value })}
          >
            <Select.Option value="" label="Seleccionar evento" />
            {customEvents.map(evt => (
              <Select.Option key={evt.event_id} value={evt.event_name} label={evt.display_name || evt.event_name} />
            ))}
          </Select>
        </div>
      )}

      {needsPattern && (
        <Input
          placeholder="Regex (ej: /productos/)"
          value={condition.pattern || ''}
          onChange={(e) => onChange(index, { ...condition, pattern: e.target.value })}
          className="flex-1"
        />
      )}

      {needsDevice && (
        <div className="flex-1">
          <Select
            id={`cond-${index}-device`}
            name={`cond-${index}-device`}
            value={condition.value || ''}
            onChange={(e) => onChange(index, { ...condition, value: e.target.value })}
          >
            <Select.Option value="" label="Dispositivo" />
            {DEVICE_OPTIONS.map(d => (
              <Select.Option key={d} value={d} label={d} />
            ))}
          </Select>
        </div>
      )}

      {needsEntrySource && (
        <div className="flex-1">
          <Select
            id={`cond-${index}-source`}
            name={`cond-${index}-source`}
            value={condition.value || ''}
            onChange={(e) => onChange(index, { ...condition, value: e.target.value })}
          >
            <Select.Option value="" label="Fuente" />
            {ENTRY_SOURCE_OPTIONS.map(s => (
              <Select.Option key={s} value={s} label={s} />
            ))}
          </Select>
        </div>
      )}

      {needsExperiment && (
        <>
          <div className="flex-1">
            <Select
              id={`cond-${index}-exp`}
              name={`cond-${index}-exp`}
              value={condition.experiment_id || ''}
              onChange={(e) => onChange(index, { ...condition, experiment_id: e.target.value, variant: undefined })}
            >
              <Select.Option value="" label="Seleccionar experimento" />
              {experiments.map(exp => (
                <Select.Option key={exp.id} value={exp.id} label={exp.name} />
              ))}
            </Select>
          </div>
          <div className="w-[160px]">
            <Select
              id={`cond-${index}-variant`}
              name={`cond-${index}-variant`}
              value={condition.variant || ''}
              onChange={(e) => onChange(index, { ...condition, variant: e.target.value })}
            >
              <Select.Option value="" label="Variante" />
              {variantOptions.map(v => (
                <Select.Option key={v} value={v} label={v} />
              ))}
            </Select>
          </div>
        </>
      )}

      {noValueNeeded && (
        <Text className="flex-1">(sin parámetros adicionales)</Text>
      )}

      <Button appearance="transparent" onClick={() => onRemove(index)}>
        <Icon source={<TrashIcon />} color="currentColor" />
      </Button>
    </div>
  )
}

// --- Main Page ---
export function CohortsPage() {
  const { toast } = useToast()
  const [cohorts, setCohorts] = useState<CohortDefinition[]>([])
  const [loading, setLoading] = useState(true)
  const [showCreate, setShowCreate] = useState(false)
  const [creating, setCreating] = useState(false)

  // Create form state
  const [newName, setNewName] = useState('')
  const [newDescription, setNewDescription] = useState('')
  const [newConditions, setNewConditions] = useState<CohortCondition[]>([
    { type: 'funnel_step' },
  ])

  // Entity data for condition dropdowns
  const [funnelSteps, setFunnelSteps] = useState<FunnelStep[]>([])
  const [experiments, setExperiments] = useState<Experiment[]>([])
  const [customEvents, setCustomEvents] = useState<CustomEventDefinition[]>([])

  // Comparison state
  const [baselineId, setBaselineId] = useState('')
  const [compareIds, setCompareIds] = useState<string[]>([])
  const [windowDays, setWindowDays] = useState(7)
  const [comparison, setComparison] = useState<CohortComparisonResult | null>(null)
  const [comparing, setComparing] = useState(false)

  useEffect(() => {
    loadCohorts()
    loadEntities()
  }, [])

  const loadEntities = async () => {
    const storeId = authService.getCurrentStoreId()
    if (!storeId) return

    try {
      const [funnelConfig, exps, eventsRes] = await Promise.all([
        funnelService.getFunnelConfig(storeId).catch(() => null),
        experimentService.getExperiments().catch(() => []),
        listCustomEvents(storeId, 'active').catch(() => ({ custom_events: [] })),
      ])
      if (funnelConfig?.steps) setFunnelSteps(funnelConfig.steps)
      setExperiments(exps)
      setCustomEvents(eventsRes.custom_events || [])
    } catch (error) {
      console.error('Failed to load entities:', error)
    }
  }

  const loadCohorts = async () => {
    try {
      setLoading(true)
      const data = await cohortService.listCohorts()
      setCohorts(data)
    } catch (error) {
      console.error('Failed to load cohorts:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleCreate = async () => {
    if (!newName.trim()) {
      toast({ title: 'Error', description: 'El nombre es requerido.', variant: 'destructive' })
      return
    }
    if (newConditions.length === 0) {
      toast({ title: 'Error', description: 'Agrega al menos una condición.', variant: 'destructive' })
      return
    }

    try {
      setCreating(true)
      // Auto-generate description from resolved entity names if user didn't provide one
      const description = newDescription.trim()
        || conditionSummary(newConditions, funnelSteps, experiments, customEvents)
      await cohortService.createCohort({
        name: newName,
        description,
        conditions: newConditions,
      })
      toast({ title: 'Cohorte creada', description: `"${newName}" fue creada exitosamente.` })
      setShowCreate(false)
      setNewName('')
      setNewDescription('')
      setNewConditions([{ type: 'funnel_step' }])
      await loadCohorts()
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error?.response?.data?.error || 'No se pudo crear la cohorte.',
        variant: 'destructive',
      })
    } finally {
      setCreating(false)
    }
  }

  const handleArchive = async (cohortId: string) => {
    try {
      await cohortService.archiveCohort(cohortId)
      toast({ title: 'Cohorte archivada' })
      await loadCohorts()
    } catch {
      toast({ title: 'Error', description: 'No se pudo archivar.', variant: 'destructive' })
    }
  }

  const handleCompare = async () => {
    if (!baselineId || compareIds.length === 0) {
      toast({ title: 'Error', description: 'Selecciona línea base y al menos una cohorte para comparar.', variant: 'destructive' })
      return
    }

    try {
      setComparing(true)
      const result = await cohortService.compareCohorts(baselineId, compareIds, windowDays)
      setComparison(result)
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error?.response?.data?.error || error?.message || 'No se pudo comparar las cohortes.',
        variant: 'destructive',
      })
    } finally {
      setComparing(false)
    }
  }

  const handleConditionChange = (index: number, cond: CohortCondition) => {
    setNewConditions(prev => prev.map((c, i) => i === index ? cond : c))
  }

  const handleConditionRemove = (index: number) => {
    setNewConditions(prev => prev.filter((_, i) => i !== index))
  }

  const toggleCompareId = (id: string) => {
    setCompareIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    )
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <Title as="h1">Cohortes</Title>
          <Text>
            Define audiencias por comportamiento y compara su rendimiento
          </Text>
        </div>
        <Button appearance="primary" onClick={() => setShowCreate(true)}>
          <Icon source={<PlusIcon />} color="currentColor" />
          Crear Cohorte
        </Button>
      </div>

      <Tabs>
        {/* --- Cohort List Tab --- */}
        <Tabs.Item label="Cohortes">
          <div className="space-y-4 pt-4">
            {loading ? (
              <div className="space-y-3">
                {[1, 2, 3].map(i => (
                  <div key={i} className="h-20 animate-pulse rounded-lg bg-muted" />
                ))}
              </div>
            ) : cohorts.length === 0 ? (
              <Card>
                <Card.Body>
                  <div className="py-12 text-center">
                    <div className="flex justify-center"><UserGroupIcon size={48} /></div>
                    <Title as="h3" className="mt-4">Sin cohortes</Title>
                    <Text className="mt-1">
                      Crea tu primera cohorte para segmentar audiencias por comportamiento.
                    </Text>
                    <div className="mt-4 flex justify-center">
                      <Button appearance="primary" onClick={() => setShowCreate(true)}>
                        <Icon source={<PlusIcon />} color="currentColor" />
                        Crear Cohorte
                      </Button>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            ) : (
              <div className="space-y-3">
                {cohorts.map(cohort => (
                  <Card key={cohort.cohort_id}>
                    <Card.Body>
                      <div className="flex items-center justify-between">
                        <div className="space-y-1">
                          <div className="flex items-center gap-2">
                            <Title as="h3">{cohort.name}</Title>
                            <div className="shrink-0 whitespace-nowrap">
                              <Tag appearance="neutral">
                                {cohort.conditions.length} {cohort.conditions.length === 1 ? 'condición' : 'condiciones'}
                              </Tag>
                            </div>
                          </div>
                          <Text>
                            {conditionSummary(cohort.conditions, funnelSteps, experiments, customEvents)}
                          </Text>
                        </div>
                        <Button
                          appearance="transparent"
                          onClick={() => handleArchive(cohort.cohort_id)}
                        >
                          <Icon source={<ArchiveIcon />} color="currentColor" />
                        </Button>
                      </div>
                    </Card.Body>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </Tabs.Item>

        {/* --- Comparison Tab --- */}
        <Tabs.Item label="Comparar">
          <div className="space-y-6 pt-4">
            {cohorts.length < 2 ? (
              <Card>
                <Card.Body>
                  <div className="py-12 text-center">
                    <div className="flex justify-center"><StatsIcon size={48} /></div>
                    <Title as="h3" className="mt-4">Necesitas al menos 2 cohortes</Title>
                    <Text className="mt-1">
                      Crea cohortes para poder comparar su rendimiento.
                    </Text>
                  </div>
                </Card.Body>
              </Card>
            ) : (
              <>
                {/* Comparison Setup */}
                <Card>
                  <Card.Header>
                    <Title as="h3">Configurar Comparación</Title>
                    <Text>Selecciona una línea base y las cohortes a comparar</Text>
                  </Card.Header>
                  <Card.Body>
                    <div className="space-y-4">
                      <div className="grid gap-4 md:grid-cols-3">
                        <div className="space-y-2">
                          <Label htmlFor="baseline-select">Línea Base</Label>
                          <Select id="baseline-select" name="baseline-select" value={baselineId} onChange={(e) => setBaselineId(e.target.value)}>
                            <Select.Option value="" label="Seleccionar cohorte base" />
                            {cohorts.map(c => (
                              <Select.Option key={c.cohort_id} value={c.cohort_id} label={c.name} />
                            ))}
                          </Select>
                        </div>

                        <div className="space-y-2">
                          <Label>Comparar con</Label>
                          <div className="space-y-1 max-h-32 overflow-y-auto rounded-md border p-2">
                            {cohorts
                              .filter(c => c.cohort_id !== baselineId)
                              .map(c => (
                                <div key={c.cohort_id} className="py-0.5">
                                  <Checkbox
                                    name={`compare-${c.cohort_id}`}
                                    label={c.name}
                                    checked={compareIds.includes(c.cohort_id)}
                                    onChange={() => toggleCompareId(c.cohort_id)}
                                  />
                                </div>
                              ))}
                          </div>
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="window-select">Ventana (días)</Label>
                          <Select id="window-select" name="window-select" value={String(windowDays)} onChange={(e) => setWindowDays(Number(e.target.value))}>
                            <Select.Option value="0" label="Hoy" />
                            {[3, 7, 14, 30].map(d => (
                              <Select.Option key={d} value={String(d)} label={`${d} días`} />
                            ))}
                          </Select>
                        </div>
                      </div>

                      <Button appearance="primary" onClick={handleCompare} disabled={comparing || !baselineId || compareIds.length === 0}>
                        {comparing ? 'Comparando...' : 'Comparar'}
                        {!comparing && <Icon source={<ArrowRightIcon />} color="currentColor" />}
                      </Button>
                    </div>
                  </Card.Body>
                </Card>

                {/* Comparison Results */}
                {comparison && (
                  <div className="space-y-4">
                    {/* Summary Cards */}
                    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                      <MetricCard
                        label="Línea Base"
                        sublabel={comparison.baseline.name}
                        value={comparison.baseline.sessions.toLocaleString()}
                        detail={`${formatRate(comparison.baseline.conversion_rate)} conv.`}
                        accent="blue"
                      />
                      {Object.values(comparison.pairwise).map(pw => (
                        <MetricCard
                          key={pw.cohort_id}
                          label={pw.name}
                          value={pw.sessions.toLocaleString()}
                          detail={`${formatRate(pw.conversion_rate)} conv.`}
                          accent={pw.is_significant && pw.lift_percentage > 0 ? 'green' : pw.is_significant && pw.lift_percentage < 0 ? 'red' : 'neutral'}
                          badge={
                            pw.is_significant
                              ? `${pw.lift_percentage > 0 ? '+' : ''}${pw.lift_percentage.toFixed(1)}%`
                              : undefined
                          }
                        />
                      ))}
                    </div>

                    {/* Detailed Pairwise Results */}
                    {Object.values(comparison.pairwise).map(pw => (
                      <Card key={pw.cohort_id}>
                        <Card.Header>
                          <div className="flex items-center justify-between gap-3 pb-2">
                            <Title as="h4">
                              <span className="inline-flex flex-wrap items-center gap-2">
                                {comparison.baseline.name}
                                <Icon source={<ArrowRightIcon />} />
                                {pw.name}
                              </span>
                            </Title>
                            {pw.is_significant ? (
                              <div className="shrink-0 whitespace-nowrap">
                                <Tag appearance="success">
                                  <Icon source={<CheckCircleIcon />} color="currentColor" />
                                  Significativo
                                </Tag>
                              </div>
                            ) : (
                              <div className="shrink-0 whitespace-nowrap">
                                <Tag appearance="neutral">
                                  <Icon source={<ExclamationTriangleIcon />} color="currentColor" />
                                  No significativo
                                </Tag>
                              </div>
                            )}
                          </div>
                        </Card.Header>
                        <Card.Body>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            <div>
                              <Text fontSize="caption">Sesiones</Text>
                              <Text fontSize="highlight" fontWeight="bold" color="neutral-textHigh">{pw.sessions.toLocaleString()}</Text>
                            </div>
                            <div>
                              <Text fontSize="caption">Conversiones</Text>
                              <Text fontSize="highlight" fontWeight="bold" color="neutral-textHigh">{pw.conversions.toLocaleString()}</Text>
                            </div>
                            <div>
                              <Text fontSize="caption">Tasa de Conversión</Text>
                              <Text fontSize="highlight" fontWeight="bold" color="neutral-textHigh">{formatRate(pw.conversion_rate)}</Text>
                            </div>
                            <div>
                              <Text fontSize="caption">Ingresos</Text>
                              <Text fontSize="highlight" fontWeight="bold" color="neutral-textHigh">{formatCurrency(pw.revenue)}</Text>
                            </div>
                          </div>

                          <div className="mt-4 flex items-center gap-6 rounded-lg bg-muted/50 p-3">
                            <div className="flex items-center gap-1.5">
                              {pw.lift_percentage > 0 ? (
                                <span className="text-emerald-600 flex items-center"><Icon source={<ArrowupIcon />} color="currentColor" /></span>
                              ) : pw.lift_percentage < 0 ? (
                                <span className="text-red-600 flex items-center"><Icon source={<ArrowDownIcon />} color="currentColor" /></span>
                              ) : (
                                <span className="text-muted-foreground flex items-center"><Icon source={<StatsIcon />} color="currentColor" /></span>
                              )}
                              <span className={`font-medium ${pw.lift_percentage > 0 ? 'text-emerald-600' : pw.lift_percentage < 0 ? 'text-red-600' : ''}`}>
                                {pw.lift_percentage > 0 ? '+' : ''}{pw.lift_percentage.toFixed(2)}% lift
                              </span>
                            </div>
                            <Text>
                              p = {pw.p_value.toFixed(4)}
                            </Text>
                            <Text>
                              z = {pw.z_score.toFixed(2)}
                            </Text>
                            {comparison.num_comparisons > 1 && (
                              <Text fontSize="caption">
                                Bonferroni &alpha; = {comparison.bonferroni_alpha.toFixed(4)}
                              </Text>
                            )}
                          </div>

                          {!pw.is_significant && (
                            <div className="mt-3">
                              <Alert appearance="warning" title="¿Por qué no es significativo?">
                                {getInsignificanceReason(pw, comparison.baseline.sessions, comparison.baseline.conversions)}
                                {' '}Esto significa que hay un {(pw.p_value * 100).toFixed(0)}% de probabilidad de que la diferencia observada sea solo azar (se necesita &lt;5%).
                              </Alert>
                            </div>
                          )}
                        </Card.Body>
                      </Card>
                    ))}

                    {/* Winner */}
                    {comparison.winner && (
                      <Card>
                        <Card.Body>
                          <div className="flex items-center gap-3">
                            <span className="text-emerald-600 flex items-center"><Icon source={<CheckCircleIcon />} color="currentColor" /></span>
                            <Text fontWeight="medium" color="neutral-textHigh">
                              Ganador: {comparison.pairwise[comparison.winner]?.name || comparison.winner}
                            </Text>
                            <Text>
                              con {formatRate(comparison.pairwise[comparison.winner]?.conversion_rate || 0)} de conversión
                              vs {formatRate(comparison.baseline.conversion_rate)} de línea base
                            </Text>
                          </div>
                        </Card.Body>
                      </Card>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </Tabs.Item>
      </Tabs>

      {/* --- Create Cohort Dialog --- */}
      <Modal open={showCreate} onDismiss={() => setShowCreate(false)}>
        <Modal.Header title="Crear Cohorte" />
        <Modal.Body>
          <Text>
            Define condiciones de comportamiento para segmentar sesiones. Todas las condiciones se combinan con AND.
          </Text>

          <div className="space-y-4 mt-3">
            <div className="space-y-2">
              <Label htmlFor="cohort-name">Nombre</Label>
              <Input
                id="cohort-name"
                placeholder="ej: Usuarios móviles que abrieron carrito"
                value={newName}
                onChange={e => setNewName(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="cohort-desc">Descripción (opcional)</Label>
              <Input
                id="cohort-desc"
                placeholder="Descripción breve"
                value={newDescription}
                onChange={e => setNewDescription(e.target.value)}
              />
            </div>

            <div className="space-y-3">
              <Label>Condiciones</Label>
              {newConditions.map((cond, i) => (
                <ConditionRow
                  key={i}
                  condition={cond}
                  index={i}
                  onChange={handleConditionChange}
                  onRemove={handleConditionRemove}
                  funnelSteps={funnelSteps}
                  experiments={experiments}
                  customEvents={customEvents}
                />
              ))}
              <Button
                appearance="neutral"
                size="small"
                onClick={() => setNewConditions(prev => [...prev, { type: 'funnel_step' }])}
              >
                <Icon source={<PlusIcon />} color="currentColor" />
                Agregar condición
              </Button>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button appearance="neutral" onClick={() => setShowCreate(false)}>
            Cancelar
          </Button>
          <Button appearance="primary" onClick={handleCreate} disabled={creating}>
            {creating ? 'Creando...' : 'Crear Cohorte'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  )
}

// --- Metric Card ---
function MetricCard({
  label,
  sublabel,
  value,
  detail,
  accent,
  badge,
}: {
  label: string
  sublabel?: string
  value: string
  detail: string
  accent: 'blue' | 'green' | 'red' | 'neutral'
  badge?: string
}) {
  const accentStyles = {
    blue: 'border-l-blue-500 bg-blue-50/50 dark:bg-blue-950/20',
    green: 'border-l-emerald-500 bg-emerald-50/50 dark:bg-emerald-950/20',
    red: 'border-l-red-500 bg-red-50/50 dark:bg-red-950/20',
    neutral: 'border-l-gray-300',
  }

  return (
    <Card padding="none">
      <div className={`h-full border-l-4 ${accentStyles[accent]} p-4 space-y-1`}>
        <div className="flex items-center justify-between gap-2">
          <Text fontWeight="medium">{label}</Text>
          {badge && (
            <div className="shrink-0 whitespace-nowrap">
              <Tag appearance={accent === 'green' ? 'success' : accent === 'red' ? 'danger' : 'neutral'}>
                {badge}
              </Tag>
            </div>
          )}
        </div>
        {sublabel && <Text fontSize="caption">{sublabel}</Text>}
        <Text className="pt-1" fontSize="highlight" fontWeight="bold" color="neutral-textHigh">{value}</Text>
        <Text>{detail}</Text>
      </div>
    </Card>
  )
}
